/**
 * Centralized Error Logger for Database Hooks and Components
 *
 * Provides multiple logging channels:
 * 1. Console-based logging for development
 * 2. Database-based logging for production monitoring
 * 3. Sentry integration for external error tracking (production only)
 *
 * Usage:
 * ```typescript
 * import { logger, logDatabaseError } from '@/utils/errorLogger';
 *
 * // New unified logger (recommended)
 * logger.error('ComponentName', 'Failed to fetch data', error, { userId: 123 });
 * logger.info('ComponentName', 'Data fetched successfully');
 *
 * // Legacy logging (backward compatible)
 * logDatabaseError('useKpiActiveStartupsV1', 'fetch active startups KPI', error);
 * ```
 *
 * Features:
 * - Development mode: Console logging only (performance-friendly)
 * - Production mode: Database logging + Sentry error tracking
 * - Silent failure: Won't disrupt user experience if logging fails
 * - Automatic metadata: Captures URL, user agent, timestamp
 * - Session tracking: Anonymous session IDs for error correlation
 * - Sentry breadcrumbs: Info/warn logs create breadcrumbs for error context
 */

import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';

/**
 * Log level types for the unified logging system
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

/**
 * Source type for log entries
 */
export type SourceType = 'frontend' | 'backend_api' | 'edge_function';

/**
 * Unified log entry interface
 */
export interface LogEntry {
  sourceType: SourceType;
  sourceIdentifier: string;
  logLevel: LogLevel;
  message: string;
  details?: Record<string, unknown>;
  correlationId?: string;
  sessionId?: string;
  userAgent?: string;
  requestUrl?: string;
  durationMs?: number;
}

/**
 * Unified Application Logger Class
 *
 * Provides centralized logging with session tracking, automatic metadata,
 * and environment-aware logging (console in dev, database in production).
 */
export class ApplicationLogger {
  private sourceType: SourceType = 'frontend';
  private sessionId: string;

  constructor() {
    // Generate or retrieve session ID (from sessionStorage)
    this.sessionId = this.getOrCreateSessionId();
  }

  /**
   * Get or create a session ID for tracking errors across the user session
   */
  private getOrCreateSessionId(): string {
    const key = 'ssn_session_id';
    let sessionId = sessionStorage.getItem(key);

    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem(key, sessionId);
    }

    return sessionId;
  }

  /**
   * Main logging method - handles both console and database logging based on environment
   *
   * @param component - Name of the component or module
   * @param level - Log level (debug, info, warn, error, critical)
   * @param message - Log message
   * @param error - Optional error object
   * @param additionalMetadata - Optional additional metadata
   */
  async log(
    component: string,
    level: LogLevel,
    message: string,
    error?: Error,
    additionalMetadata?: Record<string, unknown>
  ): Promise<void> {
    const entry: LogEntry = {
      sourceType: this.sourceType,
      sourceIdentifier: component,
      logLevel: level,
      message,
      details: {
        stack_trace: error?.stack,
        ...additionalMetadata,
      },
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      requestUrl: window.location.href,
    };

    // Development: Console logging
    if (import.meta.env.DEV) {
      this.logToConsole(entry, error);
    }

    // Production: Database logging + Sentry integration
    if (!import.meta.env.DEV) {
      await this.logToDatabase(entry);

      // Forward errors and critical logs to Sentry
      if ((level === 'error' || level === 'critical') && error) {
        this.forwardToSentry(error, entry);
      }

      // Add breadcrumbs for info/warn to help trace errors
      if (level === 'info' || level === 'warn') {
        this.addSentryBreadcrumb(entry);
      }
    }
  }

  /**
   * Forward error to Sentry with additional context
   */
  private forwardToSentry(error: Error, entry: LogEntry): void {
    try {
      Sentry.captureException(error, {
        tags: {
          component: entry.sourceIdentifier,
          logLevel: entry.logLevel,
        },
        extra: {
          message: entry.message,
          sessionId: entry.sessionId,
          ...entry.details,
        },
      });
    } catch {
      // Silent failure - Sentry issues shouldn't break the app
      console.error('[ApplicationLogger] Failed to forward to Sentry');
    }
  }

  /**
   * Add a breadcrumb to Sentry for tracing
   */
  private addSentryBreadcrumb(entry: LogEntry): void {
    try {
      Sentry.addBreadcrumb({
        category: entry.sourceIdentifier,
        message: entry.message,
        level: entry.logLevel === 'warn' ? 'warning' : 'info',
        data: entry.details,
      });
    } catch {
      // Silent failure
    }
  }

  /**
   * Log to console with colored output based on log level
   */
  private logToConsole(entry: LogEntry, error?: Error): void {
    const prefix = `[${entry.sourceIdentifier}]`;
    const levelColors: Record<LogLevel, string> = {
      debug: 'color: gray',
      info: 'color: blue',
      warn: 'color: orange',
      error: 'color: red',
      critical: 'color: red; font-weight: bold',
    };

    console.log(`%c${prefix} ${entry.message}`, levelColors[entry.logLevel]);
    if (error) console.error(error);
    if (entry.details && Object.keys(entry.details).length > 1) {
      // Only log details if there's more than just stack_trace
      console.log('Details:', entry.details);
    }
  }

  /**
   * Log to database for production monitoring
   */
  private async logToDatabase(entry: LogEntry): Promise<void> {
    try {
      const { error } = await supabase
        .schema('logs')
        .from('application_logs')
        .insert([
          {
            source_type: entry.sourceType,
            source_identifier: entry.sourceIdentifier,
            log_level: entry.logLevel,
            message: entry.message,
            details: entry.details,
            session_id: entry.sessionId,
            user_agent: entry.userAgent,
            request_url: entry.requestUrl,
            duration_ms: entry.durationMs,
          },
        ]);

      if (error) {
        console.error('[ApplicationLogger] Failed to log to database:', error);
      }
    } catch (err) {
      // Silent failure - don't disrupt user experience
      console.error('[ApplicationLogger] Exception during database logging:', err);
    }
  }

  /**
   * Debug level logging (development only, not sent to database)
   */
  debug(component: string, message: string, metadata?: Record<string, unknown>): void {
    void this.log(component, 'debug', message, undefined, metadata);
  }

  /**
   * Info level logging
   */
  info(component: string, message: string, metadata?: Record<string, unknown>): void {
    void this.log(component, 'info', message, undefined, metadata);
  }

  /**
   * Warning level logging
   */
  warn(
    component: string,
    message: string,
    error?: Error,
    metadata?: Record<string, unknown>
  ): void {
    void this.log(component, 'warn', message, error, metadata);
  }

  /**
   * Error level logging
   */
  error(
    component: string,
    message: string,
    error: Error,
    metadata?: Record<string, unknown>
  ): void {
    void this.log(component, 'error', message, error, metadata);
  }

  /**
   * Critical level logging (highest severity)
   */
  critical(
    component: string,
    message: string,
    error: Error,
    metadata?: Record<string, unknown>
  ): void {
    void this.log(component, 'critical', message, error, metadata);
  }
}

/**
 * Singleton logger instance - use this throughout the application
 *
 * @example
 * ```typescript
 * import { logger } from '@/utils/errorLogger';
 *
 * // Log an error
 * logger.error('ComponentName', 'Failed to fetch data', error);
 *
 * // Log info
 * logger.info('ComponentName', 'Data fetched successfully');
 *
 * // Log with metadata
 * logger.warn('ComponentName', 'Slow query detected', undefined, { duration: 5000 });
 * ```
 */
export const logger = new ApplicationLogger();

// ============================================================================
// LEGACY FUNCTIONS - Maintained for backward compatibility
// ============================================================================

/**
 * Log database query errors in development mode
 *
 * @deprecated Use `logger.error()` instead for new code
 * @param context - The component or hook name (e.g., 'useKpiActiveStartupsV1', 'StatisticsSection')
 * @param operation - The operation being performed (e.g., 'fetch active startups KPI', 'query scaleups data')
 * @param error - The error object or message
 *
 * @example
 * ```typescript
 * import { logDatabaseError } from '@/utils/errorLogger';
 *
 * // In a React hook
 * useEffect(() => {
 *   if (isError) {
 *     logDatabaseError('useKpiActiveStartupsV1', 'fetch active startups KPI', error);
 *   }
 * }, [isError, error]);
 * ```
 */
export const logDatabaseError = (
  context: string,
  operation: string,
  error: Error | unknown
): void => {
  logger.error(context, `Failed to ${operation}`, error as Error, {
    operation,
    legacy: true,
  });
};

/**
 * Log successful database queries in development mode (optional verbose logging)
 *
 * @deprecated Use `logger.debug()` instead for new code
 * @param context - The component or hook name
 * @param operation - The operation that succeeded
 * @param data - Optional data to log
 *
 * @example
 * ```typescript
 * import { logDatabaseSuccess } from '@/utils/errorLogger';
 *
 * logDatabaseSuccess('useKpiActiveStartupsV1', 'fetch active startups KPI', { count: 375 });
 * ```
 */
export const logDatabaseSuccess = (context: string, operation: string, data?: unknown): void => {
  if (import.meta.env.DEV && import.meta.env.VITE_LOG_QUERIES === 'true') {
    logger.debug(context, `Successfully completed ${operation}`, {
      operation,
      data,
      legacy: true,
    });
  }
};
